import { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import Cookies from "js-cookie";
import { makeHttpRequest, logout } from "helper/helper";

import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

import { decodeBearerToken } from "helper/helper";
function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [isDashboardsOpen, setIsDashboardsOpen] = useState(false);
  const [activeNestedRoute, setActiveNestedRoute] = useState(null);
  const [user, setUser] = useState({
    username: "",
    department: "",
    position: "",
  });
  const [menuNames, setMenuNames] = useState([]);

  useEffect(() => {
    if (decodeBearerToken()) {
      const decodedUser = decodeBearerToken().user;
      setUser(decodedUser);
    }
    const storedMenuNames = localStorage.getItem("side_menus");
    if (storedMenuNames) {
      const convertMenuNames = JSON.parse(storedMenuNames);
      setMenuNames(convertMenuNames);
    }
  }, []);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }
  const [activeCollapse, setActiveCollapse] = useState(null);

  // Function to handle clicking on a collapse item
  const handleCollapseItemClick = (key) => {
    // Toggle the active state
    setActiveCollapse((prevActiveCollapse) => (prevActiveCollapse === key ? null : key));
  };

  const handleNestedRouteClick = (key) => {
    setActiveNestedRoute(key);
  };

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const filteredRoutes = routes
    .map((route) => {
      if (route.collapse) {
        const filteredCollapse = route.collapse.filter((item) =>
          menuNames.some((menu) => menu.key === item.key)
        );

        if (filteredCollapse.length > 0) {
          return {
            ...route,
            collapse: filteredCollapse.map((item) => ({
              ...item,
              icon: menuNames.find((menu) => menu.key === item.key)?.icon || item.icon,
            })),
          };
        }
      } else if (menuNames.some((menu) => menu.key === route.key)) {
        return {
          ...route,
          icon: menuNames.find((menu) => menu.key === route.key)?.icon || route.icon,
        };
      }
      return null;
    })
    .filter(Boolean);

  const renderRoutes = filteredRoutes.map(
    ({ type, name, icon, collapse, key, roles, route, noCollapse }) => {
      let returnValue;
      if (type === "collapse" && noCollapse && route) {
        // Render a standalone menu item (direct link, no dropdown)
        returnValue = (
          <NavLink key={key} to={route} onClick={() => handleNestedRouteClick(key)}>
            <SidenavCollapse
              name={name}
              icon={typeof icon === "string" ? <Icon fontSize="small">{icon}</Icon> : icon}
              active={key === activeNestedRoute || collapseName === route.replace("/", "")}
            />
          </NavLink>
        );
      } else if (type === "collapse") {
        returnValue = (
          <div key={key}>
            {/* Render the collapse item with a click handler */}
            <div onClick={() => handleCollapseItemClick(key)}>
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === activeCollapse}
                nocollapse="true"
                isTopLevel={true}
              />
            </div>
            {/* Render the nested routes with a smooth slide animation */}
            <Collapse in={key === activeCollapse} timeout={300} unmountOnExit>
              {collapse &&
                collapse.length > 0 &&
                collapse.map((nestedRoute) => (
                  <MDBox
                    key={nestedRoute.key}
                    pl={2}
                    onClick={() => handleNestedRouteClick(nestedRoute.key)} // Handle click
                  >
                    <NavLink to={nestedRoute.route}>
                      <SidenavCollapse
                        icon={<Icon fontSize="small">{nestedRoute.icon}</Icon>}
                        name={nestedRoute.name}
                        active={nestedRoute.key === activeNestedRoute} // Set active state
                      />
                    </NavLink>
                  </MDBox>
                ))}
            </Collapse>
        </div>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && (
            <MDBox component="img" src={brand} alt="BIZOPS MPI Dashboard Overview" width="2rem" />
          )}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {user?.position_name && `${user?.position_name} - ${user.username} `}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox p={2} mt="auto">
        <MDButton
          component="a"
          target="_blank"
          rel="noreferrer"
          variant="gradient"
          color={sidenavColor}
          fullWidth
          onClick={handleLogout}
        >
          Log Out
        </MDButton>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
